/**
 * Converts a timestamp to relative time format (e.g., "2 hours ago", "in 3 days")
 * @param time - Unix timestamp (ms), ISO string, or Date object
 * @param locale - BCP 47 language tag (default: 'en')
 * @param options - Intl.RelativeTimeFormat options
 * @returns Formatted relative time string or empty string on error
 */
export const getRelativeTimeFromEpoch = (
  time: string | number | Date,
  locale: string = 'en',
  options?: Intl.RelativeTimeFormatOptions
): string => {
  try {
    // Input validation
    if (time == null) {
      console.warn('getRelativeTimeFromEpoch: Invalid input - time is null or undefined');
      return '';
    }

    // Parse date
    const date = time instanceof Date ? time : new Date(time);
    
    // Validate date
    if (isNaN(date.getTime())) {
      console.warn('getRelativeTimeFromEpoch: Invalid date format', time);
      return '';
    }

    // Calculate difference
    const now = Date.now();
    const diffInMs = now - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);

    // Time unit thresholds (in seconds)
    const units: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
      { unit: 'year', seconds: 31536000 },   // 365 days
      { unit: 'month', seconds: 2592000 },   // 30 days
      { unit: 'week', seconds: 604800 },     // 7 days
      { unit: 'day', seconds: 86400 },       // 24 hours
      { unit: 'hour', seconds: 3600 },       // 60 minutes
      { unit: 'minute', seconds: 60 },       // 60 seconds
      { unit: 'second', seconds: 1 },
    ];

    // Initialize formatter (consider memoization for performance)
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: 'auto',
      ...options,
    });

    // Find appropriate unit
    for (const { unit, seconds } of units) {
      const value = Math.floor(Math.abs(diffInSeconds) / seconds);
      if (value >= 1) {
        return rtf.format(-Math.sign(diffInSeconds) * value, unit);
      }
    }

    // Fallback for "just now" case
    return rtf.format(0, 'second');
    
  } catch (error) {
    console.error('getRelativeTimeFromEpoch: Unexpected error', error);
    return '';
  }
};
